import React from 'react';
import {
  SellerOrder as SellerOrderType,
  SellerOrderStatus,
} from '@shopify-multiseller/graphql/partner/types';
import { Badge } from '../../components/primitives';
import { SectionCard } from '../../components/SectionCard';
import { formatCents, formatDate } from '../../helpers/formatters';
import { cn } from '../../lib/utils';
import { TERMINAL_FAILURES } from './orderTimeline';
import { isPayoutPaid } from './payoutState';

const Row: React.FC<{
  label: React.ReactNode;
  amount: number;
  currencyCode: string;
  negative?: boolean;
  total?: boolean;
}> = ({ label, amount, currencyCode, negative = false, total = false }) => (
  <div
    className={cn(
      'flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0',
      // The row before the total must not draw a line either. `last:` only
      // clears the total's own bottom border, so the row above it kept one,
      // four pixels clear of the total's top border: a doubled hairline.
      '[&:has(+[data-total])]:border-b-0',
      total && 'mt-1 border-b-0 border-t border-border pt-3',
    )}
    data-total={total ? '' : undefined}
  >
    <span className={cn('text-sm', total ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
      {label}
    </span>
    <span
      className={cn(
        'tabular-nums text-foreground',
        total ? 'text-lg font-semibold' : 'text-sm font-medium',
      )}
    >
      {`${negative ? '−' : ''}${formatCents({ amount: Math.abs(amount), currencyCode })}`}
    </span>
  </div>
);

/**
 * How the order's total becomes the seller's money, top to bottom.
 *
 * This replaces two separate cards — "Fees & Commissions Calculation" and
 * "Payout Calculation" — which between them stated the fee total three times
 * (once as a subtotal, once as a duplicate "Fees" row directly beneath it, and
 * again as a negative line in the second card) and never actually said what
 * the seller receives without the reader doing the subtraction themselves.
 */
export const PayoutMaths: React.FC<{ order: SellerOrderType; currencyCode: string }> = ({
  order,
  currencyCode,
}) => {
  const isFulfilled = order.status === SellerOrderStatus.Fulfilled;
  const isPaid = isPayoutPaid(order);
  // Before acceptance the fees are a quote, not a ledger. The design says so
  // in three lines under "If you accept" — the figure that decides it, and the
  // word "est." on the deduction that could still move. The line-by-line
  // breakdown is not withheld: every item row above carries its own commission.
  const isQuote = order.status === SellerOrderStatus.Pending;
  const productCommission = (order.items ?? []).reduce(
    (sum, item) => sum + (item.productCommission?.amount ?? 0),
    0,
  );
  const productFees = order.totalProductFees ?? 0;

  // The rate the design states beside the row, not just the money. One rate is
  // only truthful when every item was charged the same one, so a mixed order
  // says "Product commission" and leaves the arithmetic to the item rows.
  const itemRates = Array.from(
    new Set(
      (order.items ?? [])
        .map((item) => item.productCommission?.percentage)
        .filter((pct): pct is number => pct != null && pct > 0),
    ),
  );
  const commissionRate = itemRates.length === 1 ? itemRates[0] : null;

  // What the shipping percentage was taken from. CalculatedShippingCommission
  // carries the rate and the money but not the charge they came from, and
  // "10% of shipping" leaves the seller unable to check the sum. Recovering it
  // is exact — a commission of 1.20 at 10% can only have come from 12.00 — so
  // this states a fact rather than estimating one.
  const shippingBase =
    order.shippingCommission && order.shippingCommission.percentage > 0
      ? Math.round((order.shippingCommission.amount / (order.shippingCommission.percentage / 100)) * 100) / 100
      : null;

  // "due Aug 8", not "due August 8": this is a footnote inside a sentence, and
  // the design abbreviates the month everywhere a date is not the subject.
  const dueDate = order.payout?.payoutDueDate
    ? formatDate(order.payout.payoutDueDate, { month: 'short', day: 'numeric' })
    : undefined;

  // An order that was rejected, timed out or was cancelled is not waiting for
  // anything — it has gone back to the shop. The card was still promising
  // "payable once this order is fulfilled" under a "Payout pending" badge, on
  // the same screen as a timeline saying the money was not payable.
  const isDead = TERMINAL_FAILURES.includes(order.status);
  // Refunded after the payout had already been paid: the seller holds money the
  // shop has given back to the customer. Nothing recovers it automatically, so
  // the card has to say so rather than quietly showing a smaller figure.
  const isPaidButRefunded = isPaid && !!order.refundedAfterPayout;

  // "payout A-123" where there is a reference, plain "payout" where there is
  // not, so neither sentence below has to cope with an empty id.
  const payoutRef = order.payout?.idAlias
    ? `payout ${order.payout.idAlias}`
    : 'payout';

  const footnote = isPaidButRefunded
    ? `${formatCents({ amount: order.refundedAfterPayout ?? 0, currencyCode })} was refunded after ${payoutRef} was paid, so it is owed back to the shop. Amounts are in ${currencyCode}.`
    : isPaid
      ? `Paid in ${payoutRef}. Amounts are in ${currencyCode}.`
      : isDead
        ? `This order will not be paid out. Amounts are in ${currencyCode}.`
        : isFulfilled
          ? dueDate
            ? `Paid with your next payout, due ${dueDate}. Amounts are in ${currencyCode}.`
            : `Paid with your next payout. Amounts are in ${currencyCode}.`
          : `Payable once this order is fulfilled. Amounts are in ${currencyCode}.`;

  if (isQuote) {
    return (
      <SectionCard title="Payout" testId="order-payout-maths" quiet>
        <Row label="Products total" amount={order.total} currencyCode={currencyCode} />
        <Row
          label="Fees & commission"
          amount={order.total - order.totalPayout}
          currencyCode={currencyCode}
          negative
        />
        <Row
          label="Estimated payout"
          amount={order.totalPayout}
          currencyCode={currencyCode}
          total
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Pricing calculations"
      testId="order-payout-maths"
      trailing={
        isPaid ? (
          <Badge variant="success">Payout paid</Badge>
        ) : isDead ? (
          <Badge variant="neutral">No payout</Badge>
        ) : (
          <Badge variant="warning">Payout pending</Badge>
        )
      }
    >
      <Row label="Products total" amount={order.total} currencyCode={currencyCode} />
      {productCommission > 0 && (
        <Row
          label={commissionRate == null ? 'Product commission' : `Product commission · ${commissionRate}%`}
          amount={productCommission}
          currencyCode={currencyCode}
          negative
        />
      )}
      {productFees > 0 && (
        <Row label="Product fees" amount={productFees} currencyCode={currencyCode} negative />
      )}
      {!!order.orderFee && (
        <Row label="Order fee" amount={order.orderFee} currencyCode={currencyCode} negative />
      )}
      {!!order.shippingCommission && (
        <Row
          label={
            shippingBase == null
              ? `Shipping commission · ${order.shippingCommission.percentage}% of shipping`
              : `Shipping commission · ${order.shippingCommission.percentage}% of ${formatCents({ amount: shippingBase, currencyCode })}`
          }
          amount={order.shippingCommission.amount}
          currencyCode={currencyCode}
          negative
        />
      )}
      {order.appliedDeductions?.map((deduction, index) => (
        <Row
          key={`${deduction.name}-${index}`}
          label={
            deduction.type === 'PERCENTAGE'
              ? `${deduction.name} · ${deduction.rate}% of order total`
              : deduction.name
          }
          amount={deduction.amount}
          currencyCode={currencyCode}
          negative
        />
      ))}
      {!!order.refundedPayoutTotal && (
        <Row
          label="Refunded to the customer"
          amount={order.refundedPayoutTotal}
          currencyCode={currencyCode}
          negative
        />
      )}
      {/* "You'll receive", not "Total Payout" — the seller reads this to find
          out what lands in their account. On an order that stopped, the future
          tense is a lie: the arithmetic is still worth showing, but as what it
          would have come to.

          A refund settled after the payout was paid makes "You received" false
          in the other direction: that money did land, and some of it is now
          owed back. The label says what the figure is instead of claiming a
          receipt that did not happen. */}
      <Row
        label={
          isPaidButRefunded
            ? 'Net after refund'
            : isPaid
              ? 'You received'
              : isDead
                ? 'Would have been'
                : "You'll receive"
        }
        amount={order.totalPayout}
        currencyCode={currencyCode}
        total
      />
      <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>
    </SectionCard>
  );
};
